import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Helper per estrarre messaggi di errore dalle risposte API
export const getErrorMessage = (error, defaultMessage = 'Si è verificato un errore') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || defaultMessage;
  }
  return defaultMessage;
};

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('jwt');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // utile per debug: vedrai subito se manca
    console.warn('[API] Nessun token trovato in localStorage');
  }

  return config;
});


api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo redirect su 401 se NON siamo già su una pagina pubblica (login, register, etc.)
    // e se l'errore NON proviene da una richiesta di login/register
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                           error.config?.url?.includes('/auth/register') ||
                           error.config?.url?.includes('/athletes/auth/login') ||
                           error.config?.url?.includes('/athletes/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const currentPath = window.location.pathname;
      const isPublicPath = currentPath === '/login' || 
                           currentPath === '/register' || 
                           currentPath === '/' ||
                           currentPath.includes('/forgot-password') ||
                           currentPath.includes('/reset-password') ||
                           currentPath.includes('/athlete/register');
      
      // Solo redirect se siamo su una pagina protetta
      if (!isPublicPath) {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  registerDual: (data) => api.post('/auth/register-dual', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addAthleteRole: () => api.post('/auth/add-athlete-role'),
  switchToAthlete: () => api.post('/auth/switch-to-athlete'),
  switchToCoach: () => api.post('/auth/switch-to-coach'),
  deleteAccount: () => api.delete('/auth/delete-account'),
  linkAthleteAccount: (data) => api.post('/auth/link-athlete-account', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const athleteProfilesAPI = {
  getProfiles: () => api.get('/athletes/auth/profiles'),
  switchProfile: (targetAthleteId) => api.post('/athletes/auth/switch-profile', { target_athlete_id: targetAthleteId }),
  loginAsProfile: (email, password, athleteId) => api.post('/athletes/auth/login-as-profile', { email, password, athlete_id: athleteId }),
  linkProfile: (athleteCode) => api.post('/athletes/auth/link-profile', { athlete_code: athleteCode }),
};

export const athleteAuthAPI = {
  register: (data) => api.post('/athletes/auth/register', data),
  registerWithInvite: (data, token) => api.post(`/athletes/auth/register-with-invite?invite_token=${token}`, data),
  login: (data) => api.post('/athletes/auth/login', data),
  me: () => api.get('/athletes/auth/me'),
  updateProfile: (data) => api.put('/athletes/auth/me', data),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/athletes/auth/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addCoachRole: () => api.post('/athletes/auth/add-coach-role'),
  validateInvite: (token) => api.get(`/athletes/invites/${token}`),
};

export const athletesAPI = {
  getAll: () => api.get('/athletes'),
  create: (data) => api.post('/athletes', data),
  update: (id, data) => api.put(`/athletes/${id}`, data),
  delete: (id) => api.delete(`/athletes/${id}`),
  getDetails: (id) => api.get(`/athletes/${id}/details`),
  getMyLessons: (params) => api.get('/athletes/me/lessons', { params }),
  getMyReports: (params) => api.get('/athletes/me/reports', { params }),
  updateMyProfile: (data) => api.put('/athletes/auth/me', data),
  changePassword: (data) => api.post('/athletes/auth/change-password', data),
};

export const subscriptionAPI = {
  activate: (data) => api.post('/subscription/activate', data),
};

export const venuesAPI = {
  getAll: () => api.get('/venues'),
  create: (data) => api.post('/venues', data),
  update: (id, data) => api.put(`/venues/${id}`, data),
  delete: (id) => api.delete(`/venues/${id}`),
  getDetails: (id) => api.get(`/venues/${id}/details`),
};

export const lessonsAPI = {
  getAll: () => api.get('/lessons'),
  create: (data) => api.post('/lessons', data),
  update: (id, data) => api.put(`/lessons/${id}`, data),
  delete: (id) => api.delete(`/lessons/${id}`),
  getDetails: (id) => api.get(`/lessons/${id}/details`),
  calculateCosts: (id) => api.get(`/lessons/${id}/calculate-costs`),
  markPayment: (data) => api.post('/lessons/mark-payment', data),
  unmarkPayment: (data) => api.post('/lessons/unmark-payment', data),
};

export const notificationsAPI = {
  registerDevice: (token, platform = 'web') => api.post('/notifications/register-device', { token, platform }),
  unregisterDevice: (token) => api.delete(`/notifications/unregister-device?token=${token}`),
  sendTest: (payload = {}) => api.post('/notifications/test', payload),
};

export const reportsAPI = {
  byAthlete: (params) => api.get('/reports/by-athlete', { params }),
  byDate: (params) => api.get('/reports/by-date', { params }),
};

export const venueManagerAPI = {
  invite: (data) => api.post('/venue-manager/invite', data),
  getReport: (token) => axios.get(`${API_BASE}/venue-manager/report/${token}`),
};